import React, { useState, useEffect } from 'react';
import { Star, Quote, Building2, CheckCircle2 } from 'lucide-react';
import { Language, Testimonial } from '../types';
import { getTestimonials } from '../services/testimonialsStorage';

interface TestimonialsSectionProps {
  currentLang: Language;
}

export const TestimonialsSection: React.FC<TestimonialsSectionProps> = ({ currentLang }) => {
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);

  useEffect(() => {
    setTestimonials(getTestimonials());
    
    const handleUpdate = (e: Event) => {
      const customEvent = e as CustomEvent<Testimonial[]>;
      if (customEvent.detail) {
        setTestimonials(customEvent.detail);
      }
    };
    
    window.addEventListener('chc_testimonials_updated', handleUpdate);
    return () => window.removeEventListener('chc_testimonials_updated', handleUpdate);
  }, []);
  return (
    <section className="py-20 bg-slate-50 dark:bg-[#040E14] text-slate-900 dark:text-white relative overflow-hidden border-b border-slate-200 dark:border-[#0E3A4B] transition-colors duration-200">
      
      {/* Subtle Background Glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-cyan-500/5 rounded-full blur-3xl pointer-events-none" />

      <div className="w-[90%] max-w-none mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        <div className="text-center w-[90%] lg:w-[70%] max-w-none mx-auto mb-14 space-y-3">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-cyan-50 dark:bg-[#09222E] border border-cyan-300 dark:border-cyan-500/40 text-cyan-800 dark:text-cyan-300 text-xs font-bold uppercase tracking-wider shadow-sm">
            <Star className="w-3.5 h-3.5 fill-cyan-500 text-cyan-500 dark:fill-cyan-400 dark:text-cyan-400" />
            <span>
              {currentLang === 'ar' ? 'شهادات وثقة صناعية' : currentLang === 'en' ? 'Testimonials & Industrial Trust' : 'Témoignages & Confiance Industrielle'}
            </span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-extrabold font-montserrat text-slate-900 dark:text-white">
            {currentLang === 'ar' ? 'ماذا يقول قادة المؤسسات عن خبرتنا' : currentLang === 'en' ? 'What Industry Leaders Say About Our Consulting' : 'Ce que disent les Directions Générales & Industrielles'}
          </h2>
          <p className="text-sm sm:text-base text-slate-600 dark:text-slate-300">
            {currentLang === 'ar' ? 'شهادات حقيقية من شركائنا في قطاعات الطاقة، الموانئ، الصناعة والبنوك.' : currentLang === 'en' ? 'Direct feedback from executive teams across energy, maritime, heavy industry, and banking.' : 'Retours d\'expérience des grands comptes accompagnés lors de certifications et missions d\'audit.'}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {testimonials.map((testi) => (
            <div
              key={testi.id}
              className="p-6 sm:p-8 rounded-3xl bg-white dark:bg-[#09222E]/80 border border-slate-200 dark:border-[#134659] flex flex-col justify-between hover:border-cyan-400 dark:hover:border-cyan-500/50 transition-all shadow-md dark:shadow-xl hover:-translate-y-1"
            >
              <div className="space-y-4">
                {/* Rating stars */}
                <div className="flex items-center gap-1 text-cyan-500 dark:text-cyan-400">
                  {[...Array(testi.rating)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-cyan-500 text-cyan-500 dark:fill-cyan-400 dark:text-cyan-400" />
                  ))}
                </div>

                <p className="text-xs sm:text-sm text-slate-700 dark:text-slate-300 italic leading-relaxed">
                  « {typeof testi.text === 'string' ? testi.text : (testi.text?.[currentLang] || testi.text?.fr)} »
                </p>
              </div>

              <div className="pt-6 mt-6 border-t border-slate-100 dark:border-[#134659] flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-cyan-50 dark:bg-cyan-500/15 text-cyan-700 dark:text-cyan-400 flex items-center justify-center font-bold text-xs border border-cyan-200 dark:border-cyan-500/30">
                  {testi.authorName.charAt(0)}
                </div>
                <div>
                  <div className="text-xs sm:text-sm font-bold text-slate-900 dark:text-white font-montserrat">
                    {testi.authorName}
                  </div>
                  <div className="text-[11px] text-cyan-700 dark:text-cyan-300 font-semibold">
                    {testi.role}
                  </div>
                  <div className="text-[10px] text-slate-500 dark:text-slate-400 flex items-center gap-1 mt-0.5">
                    <Building2 className="w-3 h-3 text-cyan-600 dark:text-cyan-400/80" />
                    <span>{testi.company}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

      </div>
    </section>
  );
};
